import { useState, useCallback } from 'react';
import { message } from 'antd';
import { p503Api, PendingItem } from '../../../services/p503Api';

export interface ItemState {
  item: PendingItem;
  selected: boolean;
}

export function useP503() {
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  /** Query pending report items by range */
  const handleQuery = useCallback(async (startNo: string, endNo: string) => {
    if (!startNo.trim() && !endNo.trim()) {
      message.warning('請輸入查詢範圍');
      return;
    }
    setLoading(true);
    setQueried(false);
    try {
      const res = await p503Api.query(startNo.trim(), endNo.trim());
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的待核准測試報告');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Toggle checkbox for a single item */
  const handleToggleItem = useCallback((reportNo: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fp230_01 === reportNo ? { ...s, selected: checked } : s
      )
    );
  }, []);

  /** Select all / deselect all */
  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) =>
      prev.map((s) => ({ ...s, selected: checked }))
    );
  }, []);

  /** Execute approval for all selected items */
  const handleExecute = useCallback(async (): Promise<boolean> => {
    const selectedNos = items
      .filter((s) => s.selected)
      .map((s) => s.item.fp230_01);

    if (selectedNos.length === 0) {
      message.warning('請先勾選要核准的測試報告');
      return false;
    }

    setExecuteLoading(true);
    try {
      const res = await p503Api.execute(selectedNos);
      setLastApiResponse(res);
      if (res.failed === 0) {
        message.success(res.message || `核准完成，成功 ${res.success} 筆`);
      } else {
        message.warning(
          res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
        );
        if (res.results) {
          res.results
            .filter((r) => !r.success)
            .forEach((r) => message.error(`${r.report_no}: ${r.message}`));
        }
      }
      // Clear items after execution
      setItems([]);
      setQueried(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '核准執行失敗');
      return false;
    } finally {
      setExecuteLoading(false);
    }
  }, [items]);

  const selectedCount = items.filter((s) => s.selected).length;

  return {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    lastApiResponse,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
  };
}
